import { ElementType, FC, useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { Box, Typography } from '@material-ui/core';
import Stack from '@mui/material/Stack';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { GroupIcon, Link } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';

type GroupCardContentProps = {
  groups: Entity[];
};

type GroupListProps = {
  groups: Entity[];
  filterCondition: (team: Entity) => boolean;
  icon: ElementType;
  label: string;
};

type GroupItemProps = {
  team: Team;
  icon: ElementType;
};

type ProfileInfo = {
  displayName?: string;
};

type Team = {
  metadata: {
    name: string;
  };
  kind: string;
  spec?: {
    profile?: ProfileInfo;
  };
};

const GroupItem: FC<GroupItemProps> = ({ team, icon: Icon }) => (
  <div
    key={team.metadata.name}
    style={{
      marginRight: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    }}
  >
    <Icon fontSize="small" />
    <Link
      to={`/catalog/default/${team.kind}/${team.metadata.name}`}
      variant="body1"
    >
      {team.spec?.profile?.displayName}
    </Link>
  </div>
);

const GroupList: FC<GroupListProps> = ({
  groups,
  filterCondition,
  icon,
  label,
}) => (
  <Stack direction="column">
    <Typography variant="h6">{label}</Typography>
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '10px',
      }}
    >
      {groups.some(filterCondition) ? (
        groups
          .filter(filterCondition)
          .map(team => (
            <GroupItem key={team.metadata.name} team={team} icon={icon} />
          ))
      ) : (
        <Typography>No groups assigned</Typography>
      )}
    </div>
  </Stack>
);

const GroupCardContent: FC<GroupCardContentProps> = ({ groups }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    <GroupList
      groups={groups}
      filterCondition={team =>
        team.kind === 'Group' && team.spec?.type === 'team'
      }
      icon={GroupIcon}
      label="ServiceNow"
    />
    <GroupList
      groups={groups}
      filterCondition={team =>
        team.kind === 'Group' && team.spec?.type === 'github-team'
      }
      icon={GroupIcon}
      label="GitHub"
    />
  </div>
);

export const GroupsCard = () => {
  const catalogApi = useApi(catalogApiRef);

  const { entity: user } = useEntity();
  const groupsData = useMemo(
    () => ({
      relations: user.relations ?? [],
    }),
    [user.relations],
  );

  const [status, setStatus] = useState<string>('Loading...');

  const {
    value: groups,
    loading,
    error,
  } = useAsync(async () => {
    try {
      const teamNames =
        groupsData?.relations.map(relation =>
          relation.targetRef.replace('group:default/', ''),
        ) || [];
      const response = await catalogApi.getEntities({
        filter: { 'metadata.name': teamNames },
      });

      if (response.items.length === 0) {
        setStatus("Could not find this user's groups");
      }
      return response.items;
    } catch {
      setStatus("Could not fetch this user's groups");
      return null;
    }
  }, [catalogApi, groupsData]);
  useEffect(() => {
    if (loading) {
      setStatus('Loading...');
    } else if (error || !groups) {
      setStatus("Could not fetch this user's groups");
    } else if (groups.length === 0) {
      setStatus("Could not find this user's groups");
    }
  }, [loading, error, groups]);

  return (
    <Box>
      {groups && groups.length > 0 ? (
        <GroupCardContent groups={groups} />
      ) : (
        status
      )}
    </Box>
  );
};
